import React from 'react';
import ReactDOM from 'react-dom';
import styles from '../Onboarding.module.css';
import * as utils from '../../../utils/utils';
import HQDialog from '../../HQDialog/HQDialog';
import { FiPlus } from 'react-icons/fi';

export default class OnboardingAvailability extends React.Component {

    constructor(props) {
        super(props);

        this.screens = {
            Info: 1,
            Availability: 2,
        };

        this.state = {
            currentScreen: this.screens.Info,
            availabilityItems: [''],
            activeAvailabilityIndex: 0,
            isSavingAvailability: false,

            showSkipAvailability: false,

            errorText: '',
        };
    };

    componentDidMount() {
        if (!utils.isLoggedIn()) {
            window.location.href = '/signin';
            return;
        }
    };

    componentWillUnmount() {
    };


    getErrorMessage = (error, fallbackText) => {
        if (error && typeof error === 'object' && typeof error.message === 'string' && error.message.trim() !== '') {
            return error.message;
        }

        if (typeof error === 'string' && error.trim() !== '') {
            return error;
        }

        return fallbackText;
    };

    onListItemChange = (index, value) => {
        const {
            availabilityItems,
        } = this.state;

        availabilityItems[index] = value;

        this.setState({
            availabilityItems,
            errorText: '',
        });
    };

    getExampleTargetIndex = (items, activeIndex) => {
        if (Number.isInteger(activeIndex) && activeIndex >= 0 && activeIndex < items.length) {
            return activeIndex;
        }

        const emptyIndex = items.findIndex((item) => item.trim() === '');

        if (emptyIndex >= 0) {
            return emptyIndex;
        }

        return 0;
    };

    applyExampleText = (text) => {
        const { availabilityItems, activeAvailabilityIndex } = this.state;

        const nextIndex = this.getExampleTargetIndex(availabilityItems, activeAvailabilityIndex);
        this.setState((prevState) => {
            const nextItems = [...prevState.availabilityItems];
            nextItems[nextIndex] = text;

            return {
                availabilityItems: nextItems,
                activeAvailabilityIndex: nextIndex,
                errorText: '',
            };
        });
    };

    addItem = () => {
        this.setState((prevState) => {
            const nextItems = [...prevState.availabilityItems];

            if (nextItems.length < 5) {
                nextItems.push('');
            }

            return {
                availabilityItems: nextItems,
                errorText: '',
            };
        });
    };

    getSanitizedItems = (items) => {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .map((item) => (typeof item === 'string' ? item.trim() : ''))
            .filter((item) => item !== '');
    };

    saveAvailabilityAndContinue = async () => {
        if (this.state.isSavingAvailability) {
            return;
        }

        const availabilityTexts = this.getSanitizedItems(this.state.availabilityItems);

        if (availabilityTexts.length === 0) {
            this.setState({ errorText: 'Please add at least one availability item, or skip this step.' });
            return;
        }

        this.setState({
            isSavingAvailability: true,
            errorText: '',
        });

        try {
            await utils.sendRequest('/availability/saveAvailability', {
                availabilityTexts,
            });

            this.setState({
                isSavingAvailability: false,
                errorText: '',
            }, this.props.onContinue);
        }
        catch (error) {
            console.log('save availability error', error);
            this.setState({
                isSavingAvailability: false,
                errorText: this.getErrorMessage(error, 'Could not save availability. Please try again.'),
            });
        }
    };

    renderExamples = (items) => {
        return (
            <div className={styles.examplesBox}>
                <p className={styles.examplesTitle}>Examples</p>
                <div className={styles.exampleList}>
                    {items.map((item, index) => (
                        <button
                            key={index}
                            type='button'
                            className={styles.exampleButton}
                            onClick={() => this.applyExampleText(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    showSkipAvailability = () => {
        this.setState({
            showSkipAvailability: true,
        });
    };

    renderSkipAvailabilityDialog = () => {
        const dialogNode = (
            this.state.showSkipAvailability &&
            <HQDialog
                title="Skip Availability"
                message="Are you sure you want to skip this step? Setting your availability helps us match you with relevant opportunities and people. You can always set your availability later."
                primaryButtonText="Skip"
                onPrimaryClick={this.skipAvailability}
                onCancelClick={() => this.setState({ showSkipAvailability: false })}
            />
        );

        const portalNode = typeof this.props.getDialogPortalNode === 'function'
            ? this.props.getDialogPortalNode()
            : this.props.dialogPortalNode;

        if (portalNode) {
            return ReactDOM.createPortal(dialogNode, portalNode);
        }

        return dialogNode;
    };

    skipAvailability = () => {
        this.setState({
            showSkipAvailability: false,
            errorText: '',
        }, this.props.onSkip);
    };

    renderInfoScreen = () => {
        return (
            <>
                <h2 className={styles.formMainTitle}>Availability</h2>
                <p className={styles.formMainText}>Next, briefly describe what you are looking for on HQ, whether it's a new opportunity, a mentor, collaboration, or something else.</p>

                <button
                    className={styles.primaryButton}
                    type='button'
                    onClick={() => this.setState({ currentScreen: this.screens.Availability })}
                >
                    Continue
                </button>
            </>
        );
    };

    renderAvailabilityScreen = () => {
        const {
            availabilityItems,
            isSavingAvailability,
            errorText,
        } = this.state;

        return (
            <>

                <h2 className={styles.formTitle}>Availability - Your next opportunity</h2>
                <p className={styles.formText}>Briefly describe what you're looking for</p>

                {
                    this.renderExamples([
                        'Software job, hybrid or remote in US, min $100k',
                        'Paid internship in Biology major, near Boston',
                        'Director of Marketing anywhere',
                        'Looking for a co-founder for my startup',
                        'Looking for a mentor in AI and ML',
                    ])
                }

                <div className={styles.form}>
                    {availabilityItems.map((item, index) => (
                        <div key={`availabilityItems-${index}`} className={styles.fieldGroup}>
                            <label className={styles.label} htmlFor={`availabilityItems-item-${index + 1}`}>Item {index + 1}</label>
                            <input
                                type='text'
                                id={`availabilityItems-item-${index + 1}`}
                                rows={3}
                                className={styles.textInput}
                                placeholder='Describe a role, location, work style, and compensation or timeline.'
                                value={item}
                                onChange={(event) => this.onListItemChange(index, event.target.value)}
                                onFocus={() => this.setState({ activeAvailabilityIndex: index })}
                                disabled={isSavingAvailability}
                            />
                        </div>
                    ))}

                    {
                        availabilityItems.length < 5 &&
                        <button
                            className={styles.secondaryButton}
                            type='button'
                            onClick={this.addItem}
                            disabled={isSavingAvailability}
                        >
                            <FiPlus /> Add Item
                        </button>
                    }

                    <div className={styles.actionRow}>
                        <span className={styles.helperText}>{availabilityItems.length}/5 items</span>
                    </div>

                    <button
                        className={styles.primaryButton}
                        type='button'
                        onClick={this.saveAvailabilityAndContinue}
                        disabled={isSavingAvailability}
                    >
                        {isSavingAvailability ? 'Saving...' : 'Save & Continue'}
                    </button>

                    <button
                        className={styles.secondaryButton}
                        type='button'
                        onClick={this.showSkipAvailability}
                        disabled={isSavingAvailability}
                    >
                        Skip
                    </button>
                </div>

                {
                    errorText !== '' &&
                    <div className={styles.errorBox}>{errorText}</div>
                }

                {this.renderSkipAvailabilityDialog()}
            </>
        );
    };

    renderCurrentScreen = () => {
        const {
            currentScreen,
        } = this.state;

        if (currentScreen === this.screens.Info) {
            return this.renderInfoScreen();
        }
        else if (currentScreen === this.screens.Availability) {
            return this.renderAvailabilityScreen();
        }
    };

    render() {
        return this.renderCurrentScreen();
    };

}
